// The coach's reading of Kyle's riding. Pure functions over the rides he has actually done,
// the agreed ramp and the four Paris stages: every number here is measured (from Strava) or
// computed from measured numbers, and says which.
import { goalName, goalDate, goalDays, keyDay } from "./goal.ts";
import { weekOf } from "./week.ts";
import type { Ride, State } from "./types";
import { addDays, daysBetween, mondayOf, dateLabel, weeksTo } from "./dates.ts";
import { speedKmh } from "./ride.ts";

/** A ride that counts: 15 km or more (drops test uploads and trips to the shop). */
export const real = (r: Ride) => r.km >= 15 && r.s > 0;

export function longest(rides: Ride[]) {
  return rides.filter(real).reduce<Ride | null>((a, r) => (!a || r.km > a.km ? r : a), null);
}
export function biggestClimb(rides: Ride[]) {
  return rides.filter(real).reduce<Ride | null>((a, r) => (!a || r.m > a.m ? r : a), null);
}

/** Longest run of consecutive days with a real ride. Paris is four in a row. */
export function maxConsecutiveDays(rides: Ride[]): number {
  const days = [...new Set(rides.filter(real).map((r) => r.d))].sort();
  let best = days.length ? 1 : 0, run = 1;
  for (let i = 1; i < days.length; i++) {
    run = daysBetween(days[i - 1], days[i]) === 1 ? run + 1 : 1;
    best = Math.max(best, run);
  }
  return best;
}

/** Biggest total in any seven days running. */
export function bestSevenDays(rides: Ride[]): { km: number; from: string } {
  const rs = rides.filter(real);
  let best = { km: 0, from: "" };
  for (const r of rs) {
    const end = addDays(r.d, 6);
    const km = rs.filter((x) => x.d >= r.d && x.d <= end).reduce((a, x) => a + x.km, 0);
    if (km > best.km) best = { km: Math.round(km * 10) / 10, from: r.d };
  }
  return best;
}

/** Weekly kilometres for the n weeks up to and including this one, oldest first. */
export function weeklyKm(rides: Ride[], today: string, n: number) {
  const thisMonday = mondayOf(today);
  return Array.from({ length: n }, (_, i) => {
    const w = addDays(thisMonday, -7 * (n - 1 - i));
    const end = addDays(w, 6);
    const rs = rides.filter((r) => real(r) && r.d >= w && r.d <= end);
    return { w, km: Math.round(rs.reduce((a, r) => a + r.km, 0)), rides: rs.length };
  });
}

/** The strongest stretch: the run of weeks with at least one ride, and what it averaged. */
export function bestBlock(rides: Ride[]) {
  const rs = rides.filter(real).sort((a, b) => (a.d < b.d ? -1 : 1));
  if (!rs.length) return null;
  let best: Ride[] = [], cur: Ride[] = [rs[0]];
  for (let i = 1; i < rs.length; i++) {
    if (daysBetween(rs[i - 1].d, rs[i].d) <= 10) cur.push(rs[i]);
    else { if (cur.length > best.length) best = cur; cur = [rs[i]]; }
  }
  if (cur.length > best.length) best = cur;
  const weeks = Math.max(1, Math.round(daysBetween(best[0].d, best[best.length - 1].d) / 7) + 1);
  return {
    from: best[0].d, to: best[best.length - 1].d, rides: best.length, weeks,
    perWeek: Math.round((best.length / weeks) * 10) / 10,
    avgKm: Math.round(best.reduce((a, r) => a + r.km, 0) / best.length),
  };
}

/** Pace against his own model: above 100 means faster than the climbing would predict. */
export function paceIndex(r: Ride): number {
  const actual = r.km / (r.s / 3600);
  return Math.round((actual / speedKmh(r.km, r.m)) * 100);
}
export function paceOnLongRides(rides: Ride[]) {
  const long = rides.filter((r) => real(r) && r.km >= 75);
  if (!long.length) return null;
  const kmh = long.reduce((a, r) => a + r.km, 0) / long.reduce((a, r) => a + r.s / 3600, 0);
  return { n: long.length, kmh: Math.round(kmh * 10) / 10, index: Math.round(long.reduce((a, r) => a + paceIndex(r), 0) / long.length) };
}

export type Readiness = { day: number; to: string; km: number; gain: number; distPct: number; climbPct: number; verdict: string };

/** If Paris were tomorrow: each stage against the longest ride and the biggest climbing day. */
export function parisToday(s: State): Readiness[] {
  const L = longest(s.rides)?.km ?? 0;
  const C = biggestClimb(s.rides)?.m ?? 0;
  return s.stages.map((st) => {
    const km = st.km.v ?? 0, gain = st.gain.v ?? 0;
    const distPct = Math.round((L / km) * 100), climbPct = Math.round((C / gain) * 100);
    const verdict = distPct >= 100 && climbPct >= 100 ? "Within reach now"
      : distPct >= 85 ? "Close: one longer ride away"
      : `${Math.round(km - L)} km beyond your longest`;
    return { day: st.day, to: st.to, km, gain, distPct, climbPct, verdict };
  });
}

/** Where the agreed ramp takes the long ride, and when it first reaches a distance. */
export function rampReaches(s: State, km: number) {
  const long = s.ramp.map((w) => ({ w: w.w, r: s.routes.find((x) => x.id === w.long) })).filter((x) => x.r && x.r.km >= km);
  if (!long.length) return null;
  const sunday = addDays(long[0].w, 6);
  return { date: sunday, label: dateLabel(sunday), route: long[0].r!.n, km: long[0].r!.km };
}

export type Note = { title: string; line: string; source: "measured" | "computed" };

export function readout(s: State, today: string) {
  const L = longest(s.rides), C = biggestClimb(s.rides), block = bestBlock(s.rides);
  const pace = paceOnLongRides(s.rides), seven = bestSevenDays(s.rides), run = maxConsecutiveDays(s.rides);
  const key = keyDay(s), name = goalName(s), days = goalDays(s), date = goalDate(s);
  const climbMatch = [...s.stages].sort((a, b) => (a.gain.v ?? 0) - (b.gain.v ?? 0)).find((st) => (C?.m ?? 0) >= (st.gain.v ?? 0) * 0.9);
  const reach = key?.km ? rampReaches(s, key.km * 0.95) : null;
  const last = s.rides.filter(real).map((r) => r.d).sort().pop();

  const well: Note[] = [];
  if (block) well.push({ title: "A block that held", line: `${dateLabel(block.from)} to ${dateLabel(block.to)}: ${block.rides} rides in ${block.weeks} weeks, ${block.avgKm} km on average. The habit is already proven.`, source: "measured" });
  if (pace) well.push({ title: "Pace on the long ones", line: `Your ${pace.n} rides over 75 km averaged ${pace.kmh} km/h moving, ${pace.index}% of what the climbing predicts. Distance isn't costing you speed.`, source: "computed" });
  if (C && climbMatch) well.push({ title: "Climbing", line: `${C.m} m in one ride on ${dateLabel(C.d)}: already Day ${climbMatch.day}'s ${climbMatch.gain.v} m to ${climbMatch.to}.`, source: "measured" });

  const focus: Note[] = [];
  if (L && key?.km) focus.push({ title: "Distance", line: `Your longest is ${L.km} km; ${key.label} is ${key.km}. ${reach ? `The plan takes the long ride to ${reach.km} km at ${reach.route}, ${reach.label}.` : ""}`.trim(), source: key.measured ? "measured" : "computed" });
  if (days >= 2 && name) focus.push({ title: "Days in a row", line: run <= 1 ? `${name} is ${days} days running and you haven't yet ridden two. Your best seven days is ${seven.km} km.` : `Your longest run is ${run} days; ${name} is ${days}. Your best seven days is ${seven.km} km.`, source: "measured" });
  if (C && key?.climb) focus.push({ title: "The big climbing day", line: `The biggest climbing day is ${key.climb.toLocaleString("en-GB")} m. Your biggest day is ${C.m} m, ${Math.round((C.m / key.climb) * 100)}% of it.`, source: key.measured ? "measured" : "computed" });

  const often = !block ? "" : block.perWeek < 1.5 ? "about once a week" : block.perWeek < 2.5 ? "about twice a week" : `${Math.round(block.perWeek)} times a week`;
  const words = block
    ? `In ${monthName(block.from) === monthName(block.to) ? monthName(block.from) : `${monthName(block.from)} and ${monthName(block.to)}`} you rode ${often}, ${block.avgKm} km a go. That is the habit to come back to.`
    : "No rides measured yet.";

  return { L, C, block, pace, seven, run, last, well, focus, words, reach, weeks: date ? weeksTo(today, date) : null };
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const monthName = (iso: string) => MONTHS[Number(iso.slice(5, 7)) - 1];

/** The directions the rider can point the next block in, each with its consequence in a number. */
export function directions(s: State, today: string) {
  const w = weekOf(s), key = keyDay(s), name = goalName(s), days = goalDays(s), date = goalDate(s);
  const LONG = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const perWeek = w.days.filter((d, i) => d.ride !== "no" && !d.kept && i !== w.longDay).length + (w.longDay >= 0 ? 1 : 0) + w.standing.length;
  const reach = key?.km ? rampReaches(s, key.km * 0.95) : null;
  const C = biggestClimb(s.rides)?.m ?? 0;
  const out = [{ id: "hold", title: "Hold the plan", line: `${perWeek} ride${perWeek === 1 ? "" : "s"} a week${w.longDay >= 0 ? `, the long one on ${LONG[w.longDay]}s` : ""}.${reach ? ` ${reach.route}, ${reach.km} km, on ${reach.label}.` : ""}`, gives: "The plan as agreed" }];
  if (days >= 2 && name) {
    const two = s.stages.length >= 2 ? (s.stages[0].km.v ?? 0) + (s.stages[1].km.v ?? 0) : null;
    out.push({ id: "back-to-back", title: "Ride back to back", line: `Two days together, once a month. ${two != null ? `${name}'s first two days are ${two} km on consecutive days.` : `${name} is ${days} days running.`}`, gives: `Rehearses the ${days} days` });
  }
  if (key?.climb && C < key.climb) out.push({ id: "hills", title: "Hills midweek", line: `A hilly route on one weekday ride, when the light allows. Your biggest day is ${C.toLocaleString("en-GB")} m; the biggest climbing day is ${key.climb.toLocaleString("en-GB")} m.`, gives: "Closes the climbing gap" });
  if (date && daysBetween(today, date) > 120) out.push({ id: "lighter-winter", title: "Lighter winter, bigger spring", line: "Fewer rides through the darkest weeks, the distance moved into spring.", gives: "Less to miss in the dark months" });
  return out;
}
